import { Filter, X } from "lucide-react";
import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** Trigger styles for portal filter Selects — filled primary when a filter is on. */
export function filterSelectTriggerClassName(isActive: boolean): string {
  return cn(
    "border shadow-none",
    isActive
      ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
      : "bg-background text-foreground hover:bg-primary/90 hover:text-primary-foreground",
  );
}

export function formatFiltersAppliedLabel(count: number): string {
  if (count === 1) return "1 filter applied";
  return `${count} filters applied`;
}

export function RemovableFilterBadge({
  label,
  onRemove,
}: {
  label: string;
  onRemove?: () => void;
}) {
  return (
    <Badge variant="default" className="gap-1 overflow-visible pr-1">
      {label}
      {onRemove ? (
        <button
          type="button"
          className="rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
          onClick={onRemove}
          aria-label={`Remove ${label} filter`}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </Badge>
  );
}

interface ActiveFiltersBarProps {
  appliedCount: number;
  onClearAll?: () => void;
  children?: ReactNode;
  className?: string;
  align?: "start" | "end";
  /**
   * `single-line` keeps the count, chips, and Clear all on one row even in a
   * narrow column; chips scroll horizontally instead of wrapping.
   */
  layout?: "wrap" | "single-line";
}

/**
 * Shared strip shown when any list/portal filters are active so the narrowed
 * view is hard to miss across operator, government, and registrant clients.
 */
export function ActiveFiltersBar({
  appliedCount,
  onClearAll,
  children,
  className,
  align = "start",
  layout = "wrap",
}: ActiveFiltersBarProps) {
  if (appliedCount <= 0) return null;

  const singleLine = layout === "single-line";

  return (
    <output
      aria-live="polite"
      className={cn(
        "flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-3 py-2",
        singleLine ? "flex-nowrap" : "flex-wrap",
        align === "end" && "justify-end",
        className,
      )}
    >
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-sm font-semibold text-primary",
          singleLine && "shrink-0 whitespace-nowrap",
        )}
      >
        <Filter className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {formatFiltersAppliedLabel(appliedCount)}
      </span>
      {singleLine ? (
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-x-auto">
          {children}
        </div>
      ) : (
        children
      )}
      {onClearAll ? (
        <Button
          type="button"
          variant="outline"
          size="xs"
          className={cn(
            "h-6 border-primary/40 px-2 text-primary hover:bg-primary hover:text-primary-foreground",
            singleLine && "shrink-0",
          )}
          onClick={onClearAll}
        >
          Clear all
        </Button>
      ) : null}
    </output>
  );
}
